<template>
  <div class="bg-music">
    <SectionHeader title="背景音乐" />

    <div class="bg-music__selector" @click="openDialog">
      <div
        class="bg-music__icon-wrap"
        :class="{
          'bg-music__icon-wrap--playing': isPlaying,
          'bg-music__icon-wrap--disabled': !hasBgm
        }"
        @click.stop="handleIconClick"
      >
        <SvgIcon v-if="isPlaying" name="book-pause" :size="20" color="#ffffff" />
        <SvgIcon v-else name="editor-music" :size="20" color="#6033E1" />
      </div>
      <span class="bg-music__name">{{ musicName }}</span>
      <SvgIcon name="editor-chevron-right" :size="15" color="#94A3B8" class="bg-music__arrow" />
    </div>

    <div class="bg-music__divider"></div>

    <MusicSelectDialog
      v-model="dialogVisible"
      :initial-volume="initialVolume"
      :initial-content-url="initialContentUrl"
      :initial-display-name="musicName"
      @confirm="handleMusicConfirm"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import SectionHeader from './SectionHeader.vue';
  import MusicSelectDialog from '../tool/MusicSelectDialog.vue';
  import type { MusicSelectResult } from '../tool/MusicSelectDialog.vue';
  import AudioPlayer from '@/utils/AudioPlayer';

  const props = withDefaults(
    defineProps<{
      /** 当前音乐名称（由父组件从 store 读取传入） */
      musicName?: string;
      /** 弹窗初始音量 */
      initialVolume?: number;
      /** 弹窗打开时当前 bgm 的 content URL，用于匹配预选音轨 */
      initialContentUrl?: string | null;
    }>(),
    {
      musicName: '无',
      initialVolume: 0.3,
      initialContentUrl: null
    }
  );

  const emit = defineEmits<{
    (e: 'confirm', result: MusicSelectResult): void;
  }>();

  const dialogVisible = ref(false);

  // ============================================================
  // 试听播放逻辑
  // ============================================================

  const hasBgm = computed(() => !!props.initialContentUrl);

  const playingUrl = ref<string | null>(null);
  const isPlaying = computed(() => {
    return hasBgm.value && playingUrl.value === props.initialContentUrl;
  });

  function handleIconClick() {
    if (!props.initialContentUrl) return;

    const player = AudioPlayer.instance;

    if (isPlaying.value) {
      player.stop();
      playingUrl.value = null;
    } else {
      const reset = () => {
        playingUrl.value = null;
      };
      player.play(props.initialContentUrl!, reset, reset);
      playingUrl.value = props.initialContentUrl;
    }
  }

  // 关闭弹窗时，若有试听中的音频则停止
  watch(dialogVisible, (val) => {
    if (!val) {
      AudioPlayer.instance.stop();
      playingUrl.value = null;
    }
  });

  function openDialog() {
    dialogVisible.value = true;
  }

  function handleMusicConfirm(result: MusicSelectResult) {
    emit('confirm', result);
    dialogVisible.value = false;
  }

  defineExpose({ openDialog });
</script>

<style scoped>
  .bg-music {
    width: 100%;
    box-sizing: border-box;
    padding: 20px 20px 0;
  }

  .bg-music__selector {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 15px;
    padding: 12px 14px;
    height: 68px;
    background-color: #fbf9ff;
    border-radius: 12px;
    cursor: pointer;
    box-sizing: border-box;
    transition: background-color 0.2s;
  }

  .bg-music__selector:hover {
    background-color: #f3f0ff;
  }

  .bg-music__icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background-color: #ede8ff;
    border-radius: 66px;
    flex-shrink: 0;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .bg-music__icon-wrap:hover {
    background-color: #ddd6ff;
  }

  .bg-music__icon-wrap--playing {
    background-color: #6033e1;
  }

  .bg-music__icon-wrap--playing:hover {
    background-color: #7c5cec;
  }

  .bg-music__icon-wrap--disabled {
    cursor: default;
    opacity: 0.5;
  }

  .bg-music__icon-wrap--disabled:hover {
    background-color: #ede8ff;
  }

  .bg-music__name {
    flex: 1;
    font-weight: 500;
    font-size: 13px;
    line-height: 19.5px;
    color: #334155;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .bg-music__arrow {
    flex-shrink: 0;
  }

  .bg-music__divider {
    height: 1px;
    background-color: #eee;
    margin-top: 16px;
  }
</style>
